/**
 * Field Value WebSocket Handler Component
 *
 * This component handles real-time field value changes via YJS collaboration service.
 * It subscribes to field value change events and updates the JobPasteStore accordingly.
 *
 * Features:
 * - Listens for field_value_changed messages from other users
 * - Updates JobPasteStore with new field values
 * - Handles both main data and cloned data during edit mode
 * - Filters out updates from the current user to prevent conflicts
 * - Provides comprehensive logging for debugging
 */

import { useStore } from 'hooks/useStore';
import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

const FieldValueWebSocketHandler: React.FC = () => {
  const { JobPasteStore, UserStore } = useStore();
  const { onFieldValueChange } = useYJSCollaboration();

  useEffect(() => {
    // Subscribe to field value changes from YJS collaboration
    const unsubscribeFieldValueChange = onFieldValueChange(data => {
      // Console log for debugging YJS data being received

      // Don't process updates from the current user (they already have the changes)
      const currentUserName = UserStore.userMetadata?.userInfo?.email;
      if (data.updatedBy === currentUserName) {
        return;
      }

      // Update JobPasteStore with the new field value
      JobPasteStore.handleFieldValueChangeFromYJS(
        data.roomName,
        data.jobId,
        data.fieldName,
        data.fieldValue,
        data.updatedBy,
        data.timestamp
      );
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeFieldValueChange();
    };
  }, [onFieldValueChange, JobPasteStore, UserStore]);

  // This component doesn't render anything - it's just for side effects
  return null;
};

export default observer(FieldValueWebSocketHandler);
